import NavBar from "../../components/common/NavBar";
import React, { useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import { toast } from "react-toastify";
import {
    GET_SANCTION_RULES,
    DELETE_SANCTION_RULE,
    UPDATE_SANCTION_RULE,
} from "../../components/sanction/Sanction";
import CreateSanctionRuleForm from "../../components/sanction/CreateSanctionRuleForm";
import SanctionRuleFormModal from "../../components/sanction/SanctionRuleFormModal";
import { confirmDelete } from "../../components/common/confirmDelete";
import { GlobalStyles } from "../../constants/styles";
import UpgradeNotice, { UpgradeNoticeLink } from "../../components/common/UpgradeNotice";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useActiveProfileOrgProOrElite from "../../components/common/hooks/useActiveProfileOrgProOrElite";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function SanctionRulesPage() {
    const scopeId = "kbola-sanction-rules-page";
    const colors = useThemeColors();
    const { t } = useTranslation();

    const { activeProfile } = useActiveProfile();
    const { hasAnyOrgProOrElite } = useActiveProfileOrgProOrElite();

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 12;

    const [rules, setRules] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editRule, setEditRule] = useState(null);

    useEffect(() => {
        if (!showModal) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev || "";
        };
    }, [showModal]);

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background:
                    "linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.18), rgba(0,0,0,0))",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.95)",
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: "#14151a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
            headerRight: { display: "flex", alignItems: "center", gap: 10 },

            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
                textDecoration: "none",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },

            body: { padding: 14 },

            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
                transition: "transform 120ms ease, box-shadow 120ms ease",
            },
            actionBtnEdit: {
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "#14151a",
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },

            muted: {
                color: "rgba(20,21,26,0.60)",
                fontWeight: 700,
                fontSize: 12.5,
            },

            modalBackdrop: { backgroundColor: "rgba(0,0,0,0.55)" },
            modalDialog: { maxWidth: 920 },
            modalContent: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            modalHeader: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                zIndex: 3,
            },
            modalTitleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            modalBadge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            modalTitle: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            modalSub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
            },
            modalBody: {
                padding: 14,
                background: "#fff",
                maxHeight: "calc(100vh - 220px)",
                overflowY: "auto",
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const { loading, error, refetch } = useQuery(GET_SANCTION_RULES, {
        variables: { page, pageSize },
        skip: !activeProfile || !hasAnyOrgProOrElite,
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            setRules(data?.sanctionRules?.objects || []);
            setTotalPages(data?.sanctionRules?.pages || 1);
        },
    });

    const [updateRule] = useMutation(UPDATE_SANCTION_RULE, {
        onCompleted: () => {
            setEditRule(null);
            refetch();
        },
    });

    const [deleteRule] = useMutation(DELETE_SANCTION_RULE);

    const handleDelete = (rule) => {
        confirmDelete({
            title: t("sanctionRulesPage.deleteConfirmTitle"),
            message: t("sanctionRulesPage.deleteConfirmMessage", { name: rule.name }),
            confirmLabel: t("sanctionRulesPage.deleteConfirmButton"),
            cancelLabel: t("sanctionRulesPage.deleteCancelButton"),
            danger: true,
            onConfirm: async () => {
                try {
                    const { data } = await deleteRule({ variables: { id: rule.id } });
                    if (data?.deleteSanctionRule?.success) {
                        setRules((prev) => prev.filter((w) => w.id !== rule.id));
                        toast.success(t("sanctionRulesPage.deleteSuccess"), {
                            autoClose: 3000,
                            toastId: "sanction_rule_deleted",
                            position: "top-center",
                            closeOnClick: true,
                        });
                    }
                } catch (err) {
                    toast.error(getLocalizedGraphQLError(err, t, "sanctionRulesPage.deleteFailed"), {
                        autoClose: 3000,
                        toastId: "sanction_rule_delete_error",
                        position: "top-center",
                        closeOnClick: true,
                    });
                }
            },
        });
    };

    const navBarElem = {
        pageTitle: t("sanctionRulesPage.pageTitle"),
        links: [
            { pageTitle: t("sanctionRulesPage.settings"), navLink: null, active: false },
            { pageTitle: t("sanctionRulesPage.rulesPageTitle"), navLink: null, active: true },
        ],
    };

    const handleRuleSave = async (input) => {
        try {
            const { data } = await updateRule({
                variables: { id: editRule.id, input },
            });

            const updatedRule = data?.updateSanctionRule?.sanctionRule;
            if (updatedRule) {
                setRules((prev) =>
                    prev.map((w) => (w.id === updatedRule.id ? updatedRule : w))
                );
                setEditRule(null);
                toast.success(t("sanctionRulesPage.updateSuccess"));
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "sanctionRulesPage.updateFailed"));
        }
    };

    return (
        <>
            <div className="content-wrapper" id={scopeId}>
                <NavBar navBarElem={navBarElem} />

                <section className="content">
                    {error && (
                        <div className="alert alert-danger col-6 mx-auto text-center">
                            {getLocalizedGraphQLError(error, t, "sanctionRulesPage.loadFailed")}
                        </div>
                    )}

                    {!hasAnyOrgProOrElite ? (
                        <UpgradeNotice
                            planLabel={t("sanctionRulesPage.upgradePlanLabel")}
                            title={t("sanctionRulesPage.upgradeTitle")}
                            subtitle={t("sanctionRulesPage.upgradeSubtitle")}
                            ctaLabel={t("sanctionRulesPage.upgradeCta")}
                            to="/subscription/plans"
                        >
                            {t("sanctionRulesPage.upgradeTextPrefix")}{" "}
                            <UpgradeNoticeLink to="/subscription/plans">
                                {t("sanctionRulesPage.upgradeTextLink")}
                            </UpgradeNoticeLink>
                            .
                        </UpgradeNotice>
                    ) : (
                        <div style={ui.wrap}>
                            <div style={ui.body}>
                                <PaginatedFlatTable
                                    title={t("sanctionRulesPage.tableTitle")}
                                    subtitle={t("sanctionRulesPage.tableSubtitle")}
                                    page={page}
                                    onAdd={() => setShowModal(true)}
                                    pages={totalPages}
                                    onPageChange={(p) => setPage(p)}
                                    items={rules}
                                    loading={loading}
                                    headers={[
                                        t("sanctionRulesPage.tableHeaders.name"),
                                        t("sanctionRulesPage.tableHeaders.createdBy"),
                                        t("sanctionRulesPage.tableHeaders.actions"),
                                    ]}
                                    renderRow={(rule, index) => (
                                        <tr key={rule.id || index}>
                                            <td style={{ fontWeight: 900, color: "#14151a" }}>
                                                {rule.name || t("sanctionRulesPage.noValue")}
                                            </td>
                                            <td style={ui.muted}>
                                                {rule.createdBy?.email || t("sanctionRulesPage.noValue")}
                                            </td>

                                            <td className="text-right">
                                                <div style={{ display: "inline-flex", gap: 8 }}>
                                                    <button
                                                        type="button"
                                                        style={{ ...ui.actionBtn, ...ui.actionBtnEdit }}
                                                        onClick={() => setEditRule(rule)}
                                                        title={t("sanctionRulesPage.editRule")}
                                                    >
                                                        <i className="fas fa-edit" />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        style={{ ...ui.actionBtn, ...ui.actionBtnDanger }}
                                                        onClick={() => handleDelete(rule)}
                                                        title={t("sanctionRulesPage.deleteRule")}
                                                    >
                                                        <i className="fas fa-trash" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                />
                            </div>
                        </div>
                    )}

                    {showModal && (
                        <div
                            id="kbola-create-sanction-rule-modal"
                            className="modal fade show d-block"
                            tabIndex="-1"
                            role="dialog"
                            style={ui.modalBackdrop}
                        >
                            <div
                                className="modal-dialog modal-lg modal-dialog-scrollable"
                                role="document"
                                style={ui.modalDialog}
                            >
                                <div className="modal-content" style={ui.modalContent}>
                                    <div style={ui.modalHeader}>
                                        <div style={ui.modalTitleWrap}>
                                            <span style={ui.modalBadge}>{t("sanctionRulesPage.createBadge")}</span>
                                            <div style={{ minWidth: 0 }}>
                                                <h5 style={ui.modalTitle}>{t("sanctionRulesPage.createTitle")}</h5>
                                                <p style={ui.modalSub}>
                                                    {t("sanctionRulesPage.createSubtitle")}
                                                </p>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            style={ui.closeBtn}
                                            onClick={() => setShowModal(false)}
                                            aria-label={t("textWaiverModal.close")}
                                            title={t("textWaiverModal.close")}
                                        >
                                            <i
                                                className="fas fa-times"
                                                style={{ color: colors.textOnPrimary }}
                                            />
                                        </button>
                                    </div>

                                    <div className="modal-body" style={ui.modalBody}>
                                        <CreateSanctionRuleForm
                                            onCreated={(newRule) => {
                                                setRules((prev) => [newRule, ...prev]);
                                                setPage(1);
                                                toast.success(t("sanctionRulesPage.createSuccess"));
                                            }}
                                            onClose={() => setShowModal(false)}
                                            colors={colors}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {editRule && (
                        <SanctionRuleFormModal
                            rule={editRule}
                            onClose={() => setEditRule(null)}
                            onSave={handleRuleSave}
                        />
                    )}

                    <style>{`
            #${scopeId} button:focus,
            #${scopeId} a:focus,
            #${scopeId} .btn:focus,
            #kbola-create-sanction-rule-modal button:focus {
              outline: none;
              box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
            }

            #${scopeId} button[title="${t("sanctionRulesPage.editRule").replace(/"/g, '\\"')}"]:hover,
            #${scopeId} button[title="${t("sanctionRulesPage.deleteRule").replace(/"/g, '\\"')}"]:hover {
              transform: translateY(-1px);
              box-shadow: 0 10px 20px rgba(0,0,0,0.08);
            }

            #${scopeId} table thead th {
              background: rgba(0,0,0,0.02);
              border-bottom: 1px solid rgba(0,0,0,0.06);
              color: rgba(20,21,26,0.72);
              font-size: 12.5px;
              font-weight: 900;
              text-transform: uppercase;
              letter-spacing: 0.02em;
            }
            #${scopeId} table tbody td {
              border-top: 1px solid rgba(0,0,0,0.06);
              vertical-align: middle;
              color: rgba(20,21,26,0.86);
              font-weight: 700;
              font-size: 13.5px;
            }
            #${scopeId} table tbody tr:hover {
              background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.10);
            }

            #${scopeId} i.fas,
            #${scopeId} i.far,
            #${scopeId} i.fal,
            #${scopeId} i.fa,
            #${scopeId} i[class^="fa-"],
            #${scopeId} i[class*=" fa-"] {
              font-family: "Font Awesome 5 Free" !important;
              font-weight: 900;
            }
            #${scopeId} i.fab {
              font-family: "Font Awesome 5 Brands" !important;
              font-weight: 400;
            }
          `}</style>
                </section>
            </div>
        </>
    );
}
